import { Button, Icon, Image, Typography } from "@outschool/backpack";
import { faShare } from "@outschool/icons";
import {
  KakaoLocales,
  LineLocales,
  TFunction,
  useLocale,
  useTranslation,
} from "@outschool/localization";
import { outschoolEastAsiaReferralUrl } from "@outschool/routes";
import {
  TrackedButton,
  TrackedButtonProps,
} from "@outschool/ui-components-shared";
import React from "react";

import KakaoSVG from "../../images/kakaotalk.svg";
import LineSVG from "../../images/line.svg";
import exhaustiveCheck from "../utils/exhaustiveCheck";
import { getExtraMetaData } from "./Referrals";
import { ModalContext } from "./utils";

type GetTextProps = Parameters<typeof getText>[0];

const LINE_GREEN = "#07B53B";
const KAKAO_YELLOW = "#FEE500";

export function MobileTabletReferralShareButton({
  value,
  trackingSharedProperties,
  isLoggedIn,
  modalContext,
  recipientCredit,
  usid,
  ...props
}: {
  value: string;
  usid: string;
} & TrackedButtonProps &
  Omit<GetTextProps, "t">) {
  const doesWebShareApiExist = !!navigator?.share;
  const { t } = useTranslation(
    "ui-components-website\\Sharing\\MobileTabletReferralShareButton"
  );

  const locale = useLocale();
  const showLine = LineLocales.includes(locale);
  const showKakao = KakaoLocales.includes(locale);
  const showGeneric = !showLine && !showKakao;
  const eastAsiaReferralUrl = outschoolEastAsiaReferralUrl(locale, usid);
  const url = eastAsiaReferralUrl || value;

  if (!doesWebShareApiExist) {
    return null;
  }

  const text = getText({
    isLoggedIn,
    recipientCredit,
    modalContext,
    t,
  });
  let buttonText = t("Share");
  if (value.includes("classes/")) {
    buttonText = t("Share class");
  } else if (value.includes("teacher/")) {
    buttonText = t("Share teacher");
  }

  return (
    <>
      {showKakao && (
        <CustomMobileShareButton
          textColor="common.black"
          backgroundColor={KAKAO_YELLOW}
          svgSrc={KakaoSVG}
          svgAlt={t("Kakao Talk Icon")}
          buttonText={t("Share on Kakao")}
          trackingName="share_via_kakao_web_share_api"
          trackingSharedProperties={trackingSharedProperties}
          text={text}
          url={url}
          {...props}
        />
      )}
      {showLine && (
        <CustomMobileShareButton
          textColor="common.white"
          backgroundColor={LINE_GREEN}
          svgSrc={LineSVG}
          svgAlt={t("Line Icon")}
          buttonText={t("Share on Line")}
          trackingName="share_via_line_web_share_api"
          trackingSharedProperties={trackingSharedProperties}
          text={text}
          url={url}
          {...props}
        />
      )}
      {showGeneric && (
        <GenericMobileShareButton
          trackingSharedProperties={trackingSharedProperties}
          t={t}
          text={text}
          url={url}
          buttonText={buttonText}
          {...props}
        />
      )}
    </>
  );
}

function GenericMobileShareButton({
  trackingSharedProperties,
  t,
  text,
  url,
  buttonText,
  ...props
}: {
  text: string;
  url: string;
  buttonText: string;
  t: TFunction;
} & TrackedButtonProps) {
  return (
    <TrackedButton
      trackingName="share_via_web_share_api"
      {...props}
      label={t("Share")}
      onClick={() =>
        navigator.share({
          text,
          url,
        })
      }
      trackingSharedProperties={{
        ...trackingSharedProperties,
        ...getExtraMetaData(url),
      }}
      endIcon={<Icon icon={faShare} />}
      variant="contained"
    >
      {buttonText}
    </TrackedButton>
  );
}

function CustomMobileShareButton({
  textColor,
  backgroundColor,
  svgSrc,
  svgAlt,
  buttonText,
  trackingName,
  trackingSharedProperties,
  text,
  url,
  ...props
}: {
  textColor: string;
  backgroundColor: string;
  svgSrc: string;
  svgAlt: string;
  buttonText: string;
  trackingName: string;
  text: string;
  url: string;
} & TrackedButtonProps) {
  return (
    <TrackedButton
      trackingName={trackingName}
      label={buttonText}
      onClick={() =>
        navigator.share({
          text,
          url,
        })
      }
      trackingSharedProperties={{
        ...trackingSharedProperties,
        ...getExtraMetaData(url),
      }}
      sx={{
        width: "100%",
        maxHeight: 48,
        height: "100%",
        border: "none",
        "&:hover:not(:disabled)": {
          border: "none",
        },
        "&:active:not(:disabled)": {
          border: "none",
        },
        margin: "0px",
        padding: "0px",
      }}
      {...props}
    >
      <Button
        component="span"
        color="inherit"
        sx={{
          width: "100%",
          height: "100%",
          maxHeight: 48,
          color: textColor,
          backgroundColor: backgroundColor,
          border: "none",
          "&:hover:not(:disabled)": {
            backgroundColor: backgroundColor,
            border: "none",
            filter: "brightness(0.90)",
          },
          "&:active:not(:disabled)": {
            backgroundColor: backgroundColor,
            border: "none",
            filter: "brightness(0.80)",
          },
        }}
      >
        <Typography
          sx={{
            fontWeight: "bold",
            fontSize: "1.75rem",
            flex: 1,
            textAlign: "center",
          }}
        >
          {buttonText}
        </Typography>
        <Image
          src={svgSrc}
          alt={svgAlt}
          sx={{
            position: "absolute",
            right: 0,
            marginRight: 24,
          }}
        />
      </Button>
    </TrackedButton>
  );
}

function getText({
  isLoggedIn,
  modalContext,
  recipientCredit,
  t,
}: {
  isLoggedIn: boolean;
  modalContext: ModalContext;
  recipientCredit: string;
  t: TFunction;
}) {
  const prefix = (() => {
    switch (modalContext) {
      case ModalContext.UnenrolledClass:
        return t("Check out this class on Outschool!");
      case ModalContext.EnrolledClass:
        return t("Join me in this class on Outschool!");
      case ModalContext.SharedEducatorWithFriend:
        return t("Check out this teacher on Outschool!");
      case ModalContext.SharedEducatorProfile:
        return t("Check out what I’m teaching on Outschool!");
      case ModalContext.GenericReferral:
        return t(
          "Outschool lets your kids’ curiosity run wild with classes on any topic you can imagine."
        );
      case ModalContext.FavoritesList:
        return t("Check out these classes on Outschool!");
      default:
        throw exhaustiveCheck(modalContext);
    }
  })();

  const suffix = isLoggedIn
    ? t(
        " If you’re new to Outschool, you’ll get {{recipientCredit}} referral credit with this link:",
        { recipientCredit }
      )
    : "";

  return `${prefix}${suffix}`;
}
